/* tslint:disable:no-inferrable-types no-trailing-whitespace import-spacing typedef-whitespace align whitespace quotemark */

import { DocumentWindow }          from './document.window';
import { DocumentWindowManager }   from './document.window.manager';
import { Logger }                  from '@/util/logger';
import { BlobService }             from '@/services/blob.service';
import { AuthenticationService}    from '@/services/authentication.service';
import { IdeService}               from '@/services/ide.service';
import { ConnectionViewerMetadata } from './connection.viewer.metadata';

import { ConnectionViewerComponent } from '@/components/connection.viewer.component';

import * as $                      from 'jquery';
import 'gasparesganga-jquery-loading-overlay'; /* https://gasparesganga.com/labs/jquery-loading-overlay/ */

import { first                   } from "rxjs/operators";

import {
  ComponentFactoryResolver,
  ViewContainerRef,
  EmbeddedViewRef
}                                  from '@angular/core';
import {Scenario} from "@/models/scenario/scenario";
import {basename} from "@/util/util";

/**
 *
 * @class ConnectionViewerWindow
 *
 * @note this class uses dynamic component creation to host an Angular component inside a non-Angular
 * object (i.e. DocumentWindow).
 *
 * @brief window for viewing routes through a decision point
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2024
 *
 */
export class ConnectionViewerWindow extends DocumentWindow {

  /** access to the virtual file system */
  private blobber:              BlobService = null;

  /** access to user authentication */
  private auth:       AuthenticationService = null;

  /** access to user authentication */
  private ide:                   IdeService = null;

  /** the document manager we are a tab  of */
  private manager:    DocumentWindowManager = null;

  /** the current scenario (blob key) */
  private key:              string|Scenario = '';

  /** the default decision point to focus on */
  private dp: string                        = '';

  /** the Angular component that is the actual viewer...which we are hosting */
  private viewer: ConnectionViewerComponent = null;

  /** standard constructor */
  constructor(
       meta: ConnectionViewerMetadata,
    manager: DocumentWindowManager
  ) {

    super(manager, meta, manager.layoutManager());

    Logger.debug('[ConnectionViewerWindow] constructing...');

    this.manager     = manager;
    this.auth        = this.manager.authService();
    this.blobber     = this.manager.blobService();
    this.ide         = this.manager.ideService();
    this.key         = '';
    this.dp          = meta.dp;

    if (meta.scenario) {

      this.key = meta.scenario;

    } else {

      this.key = meta.key;
    }

    this.showViewer();
  }

  /** restore the search view (on current 'focus') */
  public showViewer(): void {

    let cv: ConnectionViewerWindow = this;

    if (cv.key instanceof Scenario) {

      Logger.debug(`[ConnectionViewerWindow] => ${cv.key._tag} dp='${cv.dp}'...`);

    } else {

      Logger.debug(`[ConnectionViewerWindow] => ${cv.key} dp='${cv.dp}'...`);
    }

    const container: JQuery<HTMLElement> = $(`#${this.meta.id}`);

    if (!$(container) || !$(container).is('div')) {

      this.unReady();

      Logger.error(`[ConnectionViewerWindow] missing hosting container (${cv.meta.id})!`);

      return ;
    }

    $(container).html(`
<div id="connection_viewer_${cv.meta.id}" class="connection-viewer-wrapper">

</div>
`);

    /*
     * wait for the HTML to actually update with the component selector above, this won't create the
     * component, its just HTML :)  But, we can use Angular's component factory etc. to create the
     * component and effective "host" an Angular component inside our DocumentWindow (which isn't
     * Angular based).
     *
     */

    const checkExist = setInterval(() => {

      const dom: HTMLElement = document.getElementById(`connection_viewer_${cv.meta.id}`) as HTMLElement;

      if (dom) {

        clearInterval(checkExist);

        Logger.debug(`[ConnectionViewerWindow] inject connection viewer component ...`);

        const    view: ViewContainerRef         = cv.ide.view;
        const factory: ComponentFactoryResolver = cv.ide.componentResolver;

        const creator      = factory.resolveComponentFactory(ConnectionViewerComponent);
        const componentRef = view.createComponent(creator);

        cv.viewer                               = componentRef.instance  as ConnectionViewerComponent;

        const domElem               = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

        /* ensure the new component is parented under our container */

        dom.appendChild(domElem);

        /*  can now use 'browser' as usual ... */

        /* load the blob if there is on e*/

        cv.loadBlob(cv.key, cv.dp);

        cv.viewer.reloaded = (): void => {

          cv.updateFocus(cv.key);

          if (cv.viewer.scenario) {

            const label = cv.viewer.fileName;

            Logger.debug(`[ConnectionViewerWindow] content changed: ${label}`);

            let keyBase = label.substring(label.lastIndexOf('/') + 1);

            if (!keyBase || keyBase === '' || keyBase === label) {

              keyBase = label.substring(label.lastIndexOf('\\') + 1);

              if (!keyBase || keyBase === '' || keyBase === label) {

                keyBase = label;
              }
            }

            cv.title = `${keyBase}`;

            if (/\.[^.\/]+$/.test(cv.title)) {

              this.title = this.title.substring(0, cv.title.lastIndexOf('.'));
            }

          } else {

            Logger.debug(`[ScenarioBrowserWindow] no content.`);
          }

        };
      }

    }, 100);
  }

  private loadBlob(key: string|Scenario, dp: string = ''): void {

    let cv: ConnectionViewerWindow = this;

    /* if they gave us a Scenario...roll with it! */

    if (key instanceof Scenario) {

      try {

        Logger.debug(`[ConnectionViewerWindow] direct load scenario dp='${dp}'`);

        cv.viewer.fromText('', key, dp);

      } catch(error) {

        Logger.error(`[ConnectionViewerWindow] problem loading JSON: `, error);
      }

      cv.updateFocus(cv.key);

      return ;
    }

    /* load from a blob key ... */

    if (key === '') {

      /* nothing to do yet */

      return ;
    }

    Logger.debug(`[ConnectionViewerWindow] loading: `, key);

    cv.blobber.contents(key)
      .pipe(first())
      .subscribe(
        (content: string) => {

          try {

            Logger.debug(`[ConnectionViewerWindow] loading from blob (sz=${content.length}) dp='${dp}'...`);

            cv.viewer.fromText(content, key, dp);

          } catch(error) {

            Logger.error(`[ConnectionViewerWindow] problem loading JSON: `, error);
          }

          cv.updateFocus(cv.key);

        },
        (error) => {

          Logger.error(`[ConnectionViewerWindow] can't find content ${key}: `, error);
        });

  }

  /** update the focus to current */
  private updateFocus(key: string|Scenario) {

    let cv: ConnectionViewerWindow = this;
    cv.key                         = key;
    let label: string              = '';

    if (cv.key instanceof Scenario) {

      label = basename(cv.key._tag)

    } else {

      label = basename(cv.key);
    }

    cv.title  = label;

    if (/\.[^.\/]+$/.test(cv.title)) {

      cv.title = cv.title.substring(0, cv.title.lastIndexOf('.'));
    }
  }

  public close(implied: boolean = false) {

    Logger.debug(`[ConnectionViewerWindow] closing!`);

    if (this.viewer != null) {

      this.viewer.close();

      this.viewer = null;
    }

    /* do whatever windows normally do on closing */

    super.close(implied);
  }

}
